// src/components/pediatric/MallampatiTab.tsx
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import "../../css/MallampatiTab.css";
import api from "../../api/axiosConfig";
import ImageViewer from "../charting/ImageViewer";
import { useAuth } from "../../context/AuthContext";


import Class1 from "../../assets/Class 1.png";
import Class2 from "../../assets/Class 2.png";
import Class3 from "../../assets/Class 3.png";
import Class4 from "../../assets/Class 4.png";

const classifications = [
  {
    id: "I",
    image: Class1,
    text: "Class I is present when the soft palate, uvula, and pillars are visible.",
  },
  {
    id: "II",
    image: Class2,
    text: "Class II when the soft palate and the uvula are visible.",
  },
  {
    id: "III",
    image: Class3,
    text: "Class III when only the soft palate and base of the uvula are visible.",
  },
  {
    id: "IV",
    image: Class4,
    text: "Class IV when only the hard palate is visible.",
  },
];

interface Props {
  onSuccess: () => void;
}

const MallampatiTab: React.FC<Props> = ({ onSuccess }) => {
  const { emrNumber } = useParams<{ emrNumber: string }>();
  const { doctor } = useAuth();

  // const TEST_EMR_ID = "P-2026-000005-1";

  const [signedImageUrl, setSignedImageUrl] = useState("");
  const [selectedClass, setSelectedClass] = useState<string | null>(null);
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");

  /* ---------------- FETCH IMAGE ---------------- */
  useEffect(() => {
    if (!emrNumber) return;

    const loadImage = async () => {
      try {

        const res = await api.dataCollectionApi.get(
          `/api/forms/oral-imaging/${emrNumber}`,
        );

        const key = res.data?.data?.["Oral Imaging"]?.["Mallampati Score"]?.[0];
        if (!key) return;

        const [, folder, ...rest] = key.split("/");
        const presign = await api.S3config.get("/view-url", {
          params: { module: `oral-imaging/${folder}`, key: rest.join("/") },
          headers: { "x-internal-token": "super-secret-password" },
        });

        setSignedImageUrl(presign.data?.url || "");
      } catch (e) {
        console.error(e);
      }
    };

    loadImage();
  }, [emrNumber]);

  /* ---------------- SUBMIT ---------------- */
  const handleSubmit = async () => {
    setError("");

    if (!selectedClass || !notes.trim()) {
      setError("Please select a classification and enter additional notes.");
      return;
    }

    try {
      await api.dataCollectionApi.post("/api/teledocs/mallampati", {
        emrId: emrNumber,
        // doctorName: "Dr. John Doe",
        doctorName: doctor ? `Dr. ${doctor.name}` : null,
        classification: {
          code: selectedClass,
          title: `Class ${selectedClass}`,
          details: classifications.find((c) => c.id === selectedClass)?.text,
        },
        additionalNotes: notes,
      });

      onSuccess(); // ✅ Move to next tab
    } catch (err) {
      console.error(err);
      setError("Failed to save Mallampati data. Please try again.");
    }
  };

  return (
    <div className="mallampati-container">
      <div className="section-box">
        <h4>Image Viewer</h4>
        <p>Oral Cavity Images</p>
        <ImageViewer imageSrc={signedImageUrl} />
      </div>

      <div
        className={`section-box ${!selectedClass && error ? "section-error" : ""}`}
      >
        <h4>Select Classification</h4>
        <div className="classification-grid">
          {classifications.map((cls) => (
            <div
              key={cls.id}
              className={`classification-card ${selectedClass === cls.id ? "selected" : ""}`}
              onClick={() => setSelectedClass(cls.id)}
            >
              <img src={cls.image} alt={cls.id} />
              <strong>{cls.id}</strong>
              <p>{cls.text}</p>
            </div>
          ))}
        </div>
      </div>

      <div
        className={`section-box ${!notes.trim() && error ? "section-error" : ""}`}
      >
        <h4>Additional Notes</h4>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Enter any additional observations or notes..."
        />
      </div>

      {error && <div className="form-error">{error}</div>}

      <div className="footer-bar">
        <div className="progress-container">
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: "25%" }}></div>
          </div>
          <span>1 of 3 steps completed</span>
        </div>

        <div className="action-buttons">
          <button className="reset-btn">Reset</button>
          <button className="submit-btn" onClick={handleSubmit}>
            Submit
          </button>
        </div>
      </div>
    </div>
  );
};

export default MallampatiTab;
